import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useNotifications } from './useNotifications';
import { useProfile } from './ProfileContext';
import './AppNav.css';

export default function AppNav() {
  const { profile, loading } = useProfile();
  // Session presence is read from the existing profile context (id is set only
  // once supabase.auth resolves a session). Presentation only: no gating here.
  const signedIn = !!profile?.id;
  const [notifOpen, setNotifOpen] = useState(false);
  const { notifications, unreadCount, handleClick, markAllRead, getLabel, getColor, timeAgo } = useNotifications(profile?.id);
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [quickLinksHidden, setQuickLinksHidden] = useState(false);
const [mobileOpen, setMobileOpen] = useState(false);
const navigate = useNavigate();
const location = useLocation();
const notifRef = useRef(null);

useEffect(() => {
  setQuickLinksHidden(false);
  let lastY = window.scrollY;
  const onScroll = () => {
    if (window.innerWidth > 1100) {
      setQuickLinksHidden(false);
      return;
    }
    const y = Math.max(0, window.scrollY);
    if (y < 80 || mobileOpen || notifOpen || menuOpen || document.activeElement?.closest('.appnav-quick-links')) {
      setQuickLinksHidden(false);
      lastY = y;
      return;
    }
    if (Math.abs(y - lastY) >= 12) {
      setQuickLinksHidden(y > lastY);
      lastY = y;
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}, [location.pathname, mobileOpen, notifOpen, menuOpen]);

useEffect(() => {
  function handleOutside(e) {
    if (notifRef.current && !notifRef.current.contains(e.target)) {
      setNotifOpen(false);
    }
  }
  document.addEventListener('mousedown', handleOutside);
  return () => document.removeEventListener('mousedown', handleOutside);
}, []);

  const initials =
    `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() || '?';

  const logout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className="appnav" aria-label="Main navigation">
        <NavLink className="appnav-logo" to="/">
          <img className="appnav-brand-mark" src="/vetqualified-brand-mark.png" alt="" />
          <span className="appnav-logo-wordmark">Vet<span>Qualified</span></span>
        </NavLink>

        {/* Desktop tabs */}
        <div className="appnav-tabs">
          <NavLink to="/dashboard" className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>My profile</NavLink>
          <NavLink to="/feed"     className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>Intel Feed</NavLink>
          <NavLink to="/jobs"     className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>Jobs</NavLink>
          <NavLink to="/network"  className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>Network</NavLink>
          <NavLink to="/forums"   className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>Forums</NavLink>
          <NavLink to="/messages" className={({ isActive }) => `appnav-tab ${isActive ? 'active' : ''}`}>Messages</NavLink>
        </div>

        <div className="appnav-right">
          {/* Notification bell (signed-in members only) */}
          {signedIn && (
          <div className="nav-notif-wrap" ref={notifRef}>
  <button type="button" className="nav-notif" aria-label="Notifications" aria-expanded={notifOpen} onClick={() => { setNotifOpen(o => !o); }}>
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
    {unreadCount > 0 && <span className="notif-dot"></span>}
  </button>

  {notifOpen && (
    <div className="notif-dropdown">
      <div className="notif-dropdown-header">
        <span>Notifications</span>
        {unreadCount > 0 && (
          <button className="notif-mark-all" onClick={markAllRead}>Mark all read</button>
        )}
      </div>
      <div className="notif-list">
        {notifications.length === 0 ? (
          <div className="notif-empty">No notifications yet. Replies and connection updates will appear here.</div>
        ) : (
          notifications.map(n => (
            <button type="button"
              key={n.id}
              className={`notif-item${n.read ? '' : ' notif-unread'}`}
              onClick={() => { handleClick(n); setNotifOpen(false); }}
            >
              <span className="notif-icon" style={{ '--dot': getColor(n.type) }} />
              <div className="notif-body">
                <p className="notif-text">{getLabel(n)}</p>
                <span className="notif-time">{timeAgo(n.created_at)}</span>
              </div>
              {!n.read && <span className="notif-unread-dot"></span>}
            </button>
          ))
        )}
      </div>
    </div>
  )}
</div>
          )}

          {/* Avatar dropdown (signed-in members only) */}
          {signedIn && (
          <div className="appnav-avatar-wrap">
            <button type="button" className="appnav-avatar" aria-label="Profile menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(v => !v)}>
              {profile.avatarUrl
                ? <img src={profile.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : initials
              }
            </button>
            {menuOpen && (
              <div className="appnav-dropdown">
                <NavLink to="/dashboard" className="appnav-drop-item" onClick={() => setMenuOpen(false)}>My Profile</NavLink>
                <NavLink to="/settings"  className="appnav-drop-item" onClick={() => setMenuOpen(false)}>Settings</NavLink>
                <NavLink to="/privacy" className="appnav-drop-item" onClick={() => setMenuOpen(false)}>Privacy &amp; safety</NavLink><div className="appnav-drop-divider" />
                <button className="appnav-drop-item appnav-drop-logout" onClick={logout}>Sign Out</button>
              </div>
            )}
          </div>
          )}

          {/* Sign in: anonymous visitors on public routes (e.g. /jobs). Existing route, no gating change. */}
          {!signedIn && !loading && (
            <NavLink to="/auth" className="appnav-signin">Sign in</NavLink>
          )}

          {/* Hamburger — mobile only */}
          <button
            className={`appnav-hamburger ${mobileOpen ? 'open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
            aria-controls="appnav-mobile-drawer"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <nav className={`appnav-quick-links${quickLinksHidden ? ' is-hidden' : ''}`} aria-label="Primary mobile navigation" aria-hidden={quickLinksHidden ? 'true' : undefined}>
        <NavLink to="/forums" className={({ isActive }) => `appnav-quick-link ${isActive ? 'active' : ''}`} onClick={closeMobile} tabIndex={quickLinksHidden ? -1 : undefined}>
          <span className="appnav-quick-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16v12H9l-5 3V5Z"/><path d="M8 9h8M8 13h5"/></svg></span>
          <span>Forums</span>
        </NavLink>
        <NavLink to="/jobs" className={({ isActive }) => `appnav-quick-link ${isActive ? 'active' : ''}`} onClick={closeMobile} tabIndex={quickLinksHidden ? -1 : undefined}>
          <span className="appnav-quick-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18"/></svg></span>
          <span>Jobs</span>
        </NavLink>
        <NavLink to="/dashboard" aria-label="My profile" className={({ isActive }) => `appnav-quick-link ${isActive ? 'active' : ''}`} onClick={closeMobile} tabIndex={quickLinksHidden ? -1 : undefined}>
          <span className="appnav-quick-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg></span>
          <span>Profile</span>
        </NavLink>
      </nav>

      {/* Mobile drawer */}
      <div id="appnav-mobile-drawer" className={`appnav-mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <NavLink to="/feed"      className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>Intel Feed</NavLink>
        <NavLink to="/jobs"      className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>Jobs</NavLink>
        <NavLink to="/network"   className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>Network</NavLink>
        <NavLink to="/forums"    className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>Forums</NavLink>
        <NavLink to="/messages"  className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>Messages</NavLink>
        <NavLink to="/dashboard" className={({ isActive }) => `appnav-mobile-tab ${isActive ? 'active' : ''}`} onClick={closeMobile}>My Profile</NavLink>
        <NavLink to="/privacy" className="appnav-mobile-tab" onClick={closeMobile}>Privacy &amp; safety</NavLink>
      </div>
    </>
  );
}
